// FindRatioGeoTask.js
import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import SelectInput from "../../../../components/inputs/SelectInput";
import FractionInput, {
  formatLatexPreview
} from "../../../../components/inputs/FractionInput";

/* ===== helpers ===== */

const within = (x, y) => Math.abs(x - y) < 0.01;

function randomInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function gcd(a, b) {
  a = Math.abs(a);
  b = Math.abs(b);
  while (b !== 0) {
    const r = a % b;
    a = b;
    b = r;
  }
  return a;
}

function simplifyFrac(frac) {
  let { num, den } = frac;
  if (den < 0) {
    num = -num;
    den = -den;
  }
  const g = gcd(num, den);
  return { num: num / g, den: den / g };
}

function fracToLatex(frac) {
  const { num, den } = simplifyFrac(frac);
  if (den === 1) return `${num}`;
  const sign = num * den < 0 ? "-" : "";
  const absNum = Math.abs(num);
  const absDen = Math.abs(den);
  return `${sign}\\dfrac{${absNum}}{${absDen}}`;
}

// ładny zapis liczby jako mieszany (używane w krótkiej odpowiedzi)
function formatFractionLatex(value) {
  const sign = value < 0 ? "-" : "";
  const abs = Math.abs(value);
  const intPart = Math.floor(abs);
  const frac = abs - intPart;
  if (frac === 0) return `${sign}${intPart}`;
  for (let d = 1; d <= 20; d++) {
    const n = Math.round(frac * d);
    if (Math.abs(n / d - frac) < 0.01) {
      const g = gcd(n, d);
      const num = n / g;
      const den = d / g;
      return intPart === 0
        ? `${sign}\\dfrac{${num}}{${den}}`
        : `${sign}${intPart}\\dfrac{${num}}{${den}}`;
    }
  }
  return `${sign}${abs.toFixed(2)}`;
}

/* ===== GENERATOR =====
   q = qNum/qDen, qNum>0, qDen in {1,2,3,4}.
   a_{n1}, a_{n2} całkowite.
   ratioFrac = (qNum^k)/(qDen^k) – używane TYLKO w latexie, bez dziesiętnych.
*/

export function generateTask() {
  const k = randomInt(2, 4); // różnica indeksów 2..4
  const n1 = randomInt(1, 4); // 1..4
  const n2 = n1 + k;

  // losujemy dodatnie q (często ułamkowe)
  const qDen = Math.random() < 0.5 ? pick([2, 3, 4]) : 1;
  let qNum;
  do {
    qNum = randomInt(2, 8);
  } while (qDen > 1 && (qNum <= qDen || gcd(qNum, qDen) !== 1));

  const qPos = qNum / qDen;

  // losujemy a1 tak, by a_n były całkowite: a1 = s * m * qDen^{n2-1}
  const sign = Math.random() < 0.5 ? -1 : 1;
  const m = randomInt(1, 5);
  const base = Math.pow(qDen, n2 - 1);
  const a1 = sign * m * base;

  const a_n1 = Math.round(a1 * Math.pow(qPos, n1 - 1));
  const a_n2 = Math.round(a1 * Math.pow(qPos, n2 - 1));

  // dokładny ułamek a_n2 / a_n1
  const fracAN = simplifyFrac({ num: a_n2, den: a_n1 });

  // dokładny ułamek q^k = (qNum^k)/(qDen^k)
  const ratioFrac = simplifyFrac({
    num: Math.pow(qNum, k),
    den: Math.pow(qDen, k)
  });

  const solutionsCount = k % 2 === 0 ? 2 : 1;
  const qNeg = solutionsCount === 2 ? -qPos : null;

  return {
    type: "FindRatioGeoTask",
    data: {
      n1,
      n2,
      a_n1,
      a_n2,
      k,
      // dla sprawdzania:
      qPos,
      qNeg,
      solutionsCount,
      // dla ścisłego latexu:
      qNum,
      qDen,
      fracAN,
      ratioFrac
    }
  };
}

/* ===== TREŚĆ ===== */

export function renderTask({ data }) {
  const { n1, a_n1, n2, a_n2 } = data;

  return (
    <>
      <p>
        Dany jest ciąg geometryczny, w którym{" "}
        <InlineMath math={`a_{${n1}} = ${a_n1}`} /> oraz{" "}
        <InlineMath math={`a_{${n2}} = ${a_n2}`} />.
      </p>
      <p>
        Oblicz iloraz <InlineMath math="q" /> tego ciągu.
      </p>
      <p>
        Najpierw określ liczbę rozwiązań równania na{" "}
        <InlineMath math="q" />, a następnie podaj ich wartości.
      </p>
    </>
  );
}

/* ===== INPUT ===== */

export function renderInput(_, userInput = {}, onChange, disabled) {
  const count = userInput.count ?? "";

  return (
    <div className="flex flex-col gap-2">
      <SelectInput
        options={[
          { value: "1", label: "jedno rozwiązanie" },
          { value: "2", label: "dwa rozwiązania" }
        ]}
        value={count}
        onChange={(e) =>
          onChange({ ...userInput, count: e?.target?.value ?? e })
        }
        placeholder="Wybierz liczbę rozwiązań"
        disabled={disabled}
      />

      {(count === "1" || count === "2") && (
        <div className="flex items-center gap-2">
          <span>
            <InlineMath math={"q_1 ="} />
          </span>
          <FractionInput
            value={userInput.q1 || ""}
            onChange={(val) => onChange({ ...userInput, q1: val })}
            disabled={disabled}
            label="q₁"
          />
        </div>
      )}

      {count === "2" && (
        <div className="flex items-center gap-2">
          <span>
            <InlineMath math={"q_2 ="} />
          </span>
          <FractionInput
            value={userInput.q2 || ""}
            onChange={(val) => onChange({ ...userInput, q2: val })}
            disabled={disabled}
            label="q₂"
          />
        </div>
      )}
    </div>
  );
}

/* ===== CHECK ===== */

export function checkAnswer({ data }, userInput = {}) {
  const expectedCount = data.solutionsCount;
  const givenCount = Number(userInput.count);

  if (givenCount !== expectedCount) return false;

  const q1 = userInput.q1?.parsed;
  if (q1 == null || isNaN(q1)) return false;

  if (expectedCount === 1) {
    return within(q1, data.qPos);
  }

  const q2 = userInput.q2?.parsed;
  if (q2 == null || isNaN(q2)) return false;

  const { qPos, qNeg } = data;

  const firstMatch = within(q1, qPos) && within(q2, qNeg);
  const secondMatch = within(q1, qNeg) && within(q2, qPos);

  return firstMatch || secondMatch;
}

/* ===== KRÓTKA ODPOWIEDŹ ===== */

export function renderAnswer({ data }) {
  const { solutionsCount, qPos, qNeg } = data;

  if (solutionsCount === 1) {
    return <InlineMath math={`q = ${formatFractionLatex(qPos)}`} />;
  }

  return (
    <InlineMath
      math={`q_1 = ${formatFractionLatex(qPos)},\\ q_2 = ${formatFractionLatex(
        qNeg
      )}`}
    />
  );
}

/* ===== WYJAŚNIENIE ===== */

export function renderExplanation({ data }) {
  const {
    n1,
    n2,
    a_n1,
    a_n2,
    k,
    solutionsCount,
    qNum,
    qDen,
    fracAN,
    ratioFrac
  } = data;

  const anFracLatex = fracToLatex(fracAN);
  const ratioFracLatex = fracToLatex(ratioFrac);

  const powerEqLatex = `q^{${k}} = ${ratioFracLatex}`;
  const rootBaseLatex = `\\sqrt[${k}]{${ratioFracLatex}}`;

  const qFrac = simplifyFrac({ num: qNum, den: qDen });
  const qFracLatex = fracToLatex(qFrac);

  return (
    <div className="space-y-2">
      <p>
        <strong>Krok 1:</strong> Korzystamy ze wzoru na{" "}
        <InlineMath math="n" />-ty wyraz ciągu geometrycznego:
      </p>
      <BlockMath math={`a_n = a_1 \\cdot q^{n-1}`} />

      <p>
        <strong>Krok 2:</strong> Dla dwóch wyrazów dzielimy stronami:
      </p>
      <BlockMath
        math={`\\frac{a_{${n2}}}{a_{${n1}}} = \\frac{a_1 q^{${n2}-1}}{a_1 q^{${n1}-1}} = q^{${n2}-${n1}} = q^{${k}}`}
      />
      <BlockMath
        math={`\\frac{a_{${n2}}}{a_{${n1}}} = \\frac{${a_n2}}{${a_n1}} = ${anFracLatex}`}
      />
      <BlockMath math={powerEqLatex} />

      {solutionsCount === 1 ? (
        <>
          <p>
            <strong>Krok 3:</strong> Różnica indeksów{" "}
            <InlineMath math="k" /> jest{" "}
            <strong>nieparzysta</strong>, więc równanie ma jedno rozwiązanie:
          </p>
          <BlockMath math={`q = ${rootBaseLatex} = ${qFracLatex}`} />
        </>
      ) : (
        <>
          <p>
            <strong>Krok 3:</strong> Różnica indeksów{" "}
            <InlineMath math="k" /> jest <strong>parzysta</strong>, więc
            równanie ma dwa rozwiązania:
          </p>
          <BlockMath math={`q = \\pm ${rootBaseLatex}`} />
          <BlockMath
            math={`q_1 = ${qFracLatex},\\quad q_2 = -${qFracLatex}`}
          />
        </>
      )}
    </div>
  );
}

/* ===== ODPOWIEDŹ UŻYTKOWNIKA ===== */

export function renderUserAnswer(userInput = {}) {
  const count = userInput.count;
  if (!count) return <i>(brak odpowiedzi)</i>;

  const parts = [];

  parts.push(
    <div key="c">
      <strong>Liczba rozwiązań:</strong>{" "}
      {count === "1" ? "jedno rozwiązanie" : "dwa rozwiązania"}
    </div>
  );

  if (count === "1" || count === "2") {
    const q1 = userInput.q1?.raw
      ? formatLatexPreview(userInput.q1.raw, "q_1")
      : null;
    parts.push(
      <div key="q1" style={{ marginTop: "0.5rem" }}>
        {q1 ? <InlineMath math={q1} /> : <i>(brak q₁)</i>}
      </div>
    );
  }

  if (count === "2") {
    const q2 = userInput.q2?.raw
      ? formatLatexPreview(userInput.q2.raw, "q_2")
      : null;
    parts.push(
      <div key="q2" style={{ marginTop: "0.5rem" }}>
        {q2 ? <InlineMath math={q2} /> : <i>(brak q₂)</i>}
      </div>
    );
  }

  return (
    <div style={{ textAlign: "left", marginTop: "0.5rem" }}>{parts}</div>
  );
}
